import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { resolveLocation, stateMatchesPolygon } from "./Stage3GeoData";

const Stage3GeographyMapIndia = ({ selectedNode }) => {
    const containerRef = useRef(null);
    const instanceRef = useRef(null);

    useEffect(() => {
        const loadMap = () => {
            if (
                !window.am5 ||
                !window.am5map ||
                !window.am5geodata_worldLow ||
                !window.am5geodata_indiaLow
            ) {
                setTimeout(loadMap, 100);
                return;
            }

            if (instanceRef.current) return;

            const root = window.am5.Root.new(containerRef.current);
            root.setThemes([window.am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                window.am5map.MapChart.new(root, {
                    panX: "translateX",
                    panY: "translateY",
                    projection: window.am5map.geoMercator(),
                })
            );

            const worldSeries = chart.series.push(
                window.am5map.MapPolygonSeries.new(root, {
                    geoJSON: window.am5geodata_worldLow,
                    exclude: ["AQ"], // exclude Antarctica
                })
            );

            const indiaSeries = chart.series.push(
                window.am5map.MapPolygonSeries.new(root, {
                    geoJSON: window.am5geodata_indiaLow,
                })
            );

            [worldSeries, indiaSeries].forEach((series) => {
                series.mapPolygons.template.setAll({
                    fill: window.am5.color(0xd3d3d3),
                    stroke: window.am5.color(0xffffff),
                    strokeWidth: 0.8,
                    tooltipText: "{name}",
                });
            });

            chart.appear(1000, 100);
            instanceRef.current = { root, chart, indiaSeries, worldSeries };
        };

        loadMap();

        return () => {
            if (instanceRef.current?.root) {
                instanceRef.current.root.dispose();
                instanceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!selectedNode) return;

        let cancelled = false;

        const apply = () => {
            if (cancelled) return;
            // map may still be booting — retry until the amCharts instance exists
            if (!instanceRef.current) {
                setTimeout(apply, 150);
                return;
            }

            const { indiaSeries, worldSeries, chart } = instanceRef.current;

            const raw =
                typeof selectedNode === "string"
                    ? selectedNode
                    : selectedNode.name || "";

            const loc = resolveLocation(raw);

            const grey = window.am5.color(0xd3d3d3);
            const hot = window.am5.color(0xff5733);

            // Reset all fills first
            indiaSeries.mapPolygons.each((p) => p.set("fill", grey));
            worldSeries.mapPolygons.each((p) => p.set("fill", grey));

            if (!loc.matched) return;

            if (loc.kind === "country" && loc.label !== "india") {
                // highlight a neighbouring country on the world layer
                worldSeries.mapPolygons.each((p) => {
                    if (stateMatchesPolygon(p.dataItem?.dataContext?.name, loc.label)) {
                        p.set("fill", hot);
                    }
                });
            } else if (loc.label === "india") {
                indiaSeries.mapPolygons.each((p) => p.set("fill", hot));
            } else {
                // region / state / city -> highlight the owning state(s)
                indiaSeries.mapPolygons.each((p) => {
                    const nm = p.dataItem?.dataContext?.name;
                    if (loc.states.some((s) => stateMatchesPolygon(nm, s))) {
                        p.set("fill", hot);
                    }
                });
            }

            if (loc.point) {
                const zoom =
                    loc.kind === "city"
                        ? 7
                        : loc.kind === "state"
                            ? 6
                            : loc.kind === "region"
                                ? 5
                                : 4;
                chart.zoomToGeoPoint(
                    { latitude: loc.point.lat, longitude: loc.point.lon },
                    zoom,
                    true
                );
            }
        };

        apply();
        return () => {
            cancelled = true;
        };
    }, [selectedNode]);

    return <MapWrapper ref={containerRef} />;
};

export default Stage3GeographyMapIndia;

const MapWrapper = styled.div`
  width: 100%;
  height: 140px; /* was 300px */
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
`;
